import { QueryHasWhere, Where } from '../../basic-features/where/where';
import { Query, SelectableFromShape } from '../../query';
import { Column } from '../../../columns/column';
import {
  PickQueryScopes,
  PickQuerySelectableShapeRelationsWithData,
} from '../../pick-query-types';
import { setObjectValueImmutable } from '../../../utils';
import { _clone } from '../../basic-features/clone/clone';
import { QueryDataScopes } from '../../query-data';

// Scopes data stored in table instance. Doesn't change after defining a table.
export type QueryScopes<Keys extends string> = {
  [K in Keys]: unknown;
};

export interface ScopeArgumentQuery<
  Table extends string | undefined,
  Shape extends Column.QueryColumns,
>
  extends Where, PickQuerySelectableShapeRelationsWithData {
  __isQuery: true;
  table: Table;
  shape: Shape;
  __selectable: SelectableFromShape<Shape, Table>;
}

export const _unscope = (q: Query, scope: PropertyKey): Query => {
  if (q.q.scopes) {
    q.q.scopes = { ...q.q.scopes };
    delete q.q.scopes[scope as string];
    for (const _ in q.q.scopes) {
      return q as never;
    }
    delete q.q.scopes;
  }

  return q as never;
};

/**
 * This feature allows defining a set of query modifiers to use it later.
 * Only [where conditions](/guide/where.html) can be set in a scope.
 * If you define a scope with name `default`, it will be applied for all table queries by default.
 *
 * ```ts
 * import { BaseTable } from './baseTable';
 *
 * export class SomeTable extends BaseTable {
 *   readonly table = 'some';
 *   columns = this.setColumns((t) => ({
 *     id: t.identity().primaryKey(),
 *     hidden: t.boolean(),
 *     active: t.boolean(),
 *   }));
 *
 *   scopes = this.setScopes({
 *     default: (q) => q.where({ hidden: false }),
 *     active: (q) => q.where({ active: true }),
 *   });
 * }
 *
 * const db = orchidORM(
 *   { databaseURL: '...' },
 *   {
 *     some: SomeTable,
 *   },
 * );
 *
 * // the default scope is applied for all queries:
 * const nonHiddenRecords = await db.some;
 * ```
 */
export class QueryScope {
  /**
   * See {@link QueryScope}
   *
   * Use the `scope` method to apply a pre-defined scope.
   *
   * ```ts
   * // use the `active` scope that is defined in the table:
   * await db.some.scope('active');
   * ```
   *
   * @param scope - name of the scope to apply
   */
  scope<T extends PickQueryScopes>(
    this: T,
    scope: keyof T['__scopes'],
  ): T & QueryHasWhere {
    const q = _clone(this);

    if (!q.q.scopes?.[scope as string]) {
      const s = (q.internal.scopes as QueryDataScopes)[scope as string];

      if (!s) throw new Error(`Scope ${scope as string} is not defined`);

      setObjectValueImmutable(q.q, 'scopes', scope, s);
    }

    return q as never;
  }

  /**
   * See {@link QueryScope}
   *
   * Remove conditions that were added by the scope from the query.
   *
   * ```ts
   * // SomeTable has a default scope, ignore it for this query:
   * await db.some.unscope('default');
   * ```
   *
   * @param scope - name of the scope to remove from the query
   */
  unscope<T extends PickQueryScopes>(this: T, scope: keyof T['__scopes']): T {
    return _unscope(_clone(this), scope) as never;
  }
}
